import { Tooltip } from 'bootstrap';
import notificationManager from '../utils/NotificationManager.js';
import configStore from '../tabs/fanChart/fanConfigStore.js';
import rootPersonStore from '../common/stores/rootPersonStore.js';
import authStore from '../common/stores/authStore.js';
import gedcomDataStore from './stores/gedcomDataStore.js';
import familyTownsStore from '../tabs/familyMap/stores/familyTownsStore.js';
import { resetBeforeNewGedcom } from '../utils/resetManager.js';
import { escapeHtml } from '../common/utils/escapeHtml.js';
// 🚀 OPTIMISÉ: Imports réduits - Virtual Select utilise maintenant le chargement asynchrone
import { getName, getSurname } from './formatters/individualFormatters.js';
import { storeEvents, EVENTS } from '../common/stores/storeEvents.js';
import overlayManager from '../utils/OverlayManager.js';
import { logger } from '../utils/debug/logger.js';


/* Code pour gérer l'upload des fichiers GEDCOM avec un Web Worker */
let isLoadingFile = false;
let gedcomFileName = "";
let currentModal = null;
let uploadManager = null; // Variable pour stocker l'instance du gestionnaire d'upload
// ✅ SUPPRIMÉ: userEnteredFamilyName - plus utilisé

const MODAL_STEPS = {
    SAVE_REQUEST: {
        title: 'Enregistrer le fichier',
        content: `
            <div class="modal-body">
                Voulez-vous enregistrer votre fichier pour un usage ultérieur ?
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-secondary" data-action="no">Non</button>
                <button type="button" class="btn btn-primary" data-action="yes">Oui</button>
            </div>
        `
    },
    FAMILY_NAME: {
        title: 'Nom de la famille',
        content: `
            <form id="familyNameForm">
                <div class="modal-body">
                    À quelle famille correspond votre fichier Gedcom ?
                    <input type="text" class="form-control" id="familyNameInput" required>
                </div>
                <div class="modal-footer">
                    <button type="submit" class="btn btn-primary">Enregistrer</button>
                </div>
            </form>
        `
    },
    UPLOAD_SUCCESS: {
        title: 'Téléchargement terminé',
        content: `
            <div class="modal-body">
                Votre fichier a été enregistré avec succès !
            </div>
            <div class="modal-footer">
                <button type="button" class="btn btn-primary" data-action="close">Fermer</button>
            </div>
        `
    },
    UPLOADING: {
        title: 'Téléchargement en cours',
        content: `
            <div class="modal-body">
                <div class="text-center">
                    <div class="spinner-border" role="status">
                        <span class="visually-hidden">Chargement...</span>
                    </div>
                    <p class="mt-2">Téléchargement de votre fichier en cours...</p>
                    <div class="progress mt-3">
                        <div id="uploadProgressBar" class="progress-bar" role="progressbar" style="width: 0%"></div>
                    </div>
                    <p id="uploadProgressText" class="mt-1">0%</p>
                </div>
            </div>
        `
    }
};

/**
 * Met à jour le bouton info GEDCOM avec les informations du fichier
 * @param {number} individualsCount - Nombre d'individus dans le GEDCOM
 */
function updateGedcomInfoButton(individualsCount) {
    const button = document.getElementById('gedcomInfoButton');
    if (!button) return;

    // Afficher le bouton
    button.style.display = 'inline-block';

    // Préparer le contenu du tooltip
    const fileName = escapeHtml(gedcomFileName) || 'Fichier GEDCOM';
    const tooltipContent = `
        <div style="text-align: left;">
            <strong>${fileName}</strong><br>
            <span style="color: #aaa;">${individualsCount} ${individualsCount > 1 ? 'personnes' : 'personne'}</span>
        </div>
    `;

    // Mettre à jour le titre du tooltip
    button.setAttribute('data-bs-original-title', tooltipContent);
    button.setAttribute('title', tooltipContent);

    // Réinitialiser le tooltip Bootstrap s'il existe déjà
    const existingTooltip = Tooltip.getInstance(button);
    if (existingTooltip) {
        existingTooltip.dispose();
    }

    // Créer un nouveau tooltip
    new Tooltip(button, {
        html: true,
        placement: 'bottom'
    });
}

/**
 * Affiche une modale avec les gestionnaires d'événements configurés
 * @param {string} step - Étape de la modale à afficher
 * @param {Object} handlers - Gestionnaires d'événements pour les éléments de la modale
 */
function showModal(step, handlers = {}) {
    if (!currentModal) {
        const dialog = document.createElement('dialog');
        dialog.id = 'gedcomModal';
        dialog.className = 'app-dialog';
        dialog.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title"></h5>
                    <button type="button" class="btn-close" data-action="dialog-close" aria-label="Fermer"></button>
                </div>
                <div id="modalStepContent"></div>
            </div>`;
        dialog.addEventListener('click', (e) => {
            if (e.target.closest('[data-action="dialog-close"]')) dialog.close();
        });
        document.body.appendChild(dialog);
        currentModal = dialog;
    }

    const stepConfig = MODAL_STEPS[step];
    currentModal.querySelector('.modal-title').textContent = stepConfig.title;
    currentModal.querySelector('#modalStepContent').innerHTML = stepConfig.content;

    const oldContent = currentModal.querySelector('#modalStepContent');
    const newContent = oldContent.cloneNode(true);
    oldContent.parentNode.replaceChild(newContent, oldContent);

    Object.entries(handlers).forEach(([selector, handler]) => {
        const element = currentModal.querySelector(selector);
        if (element) {
            if (selector === 'form') {
                element.onsubmit = (e) => {
                    e.preventDefault();
                    handler(e);
                };
            } else {
                element.onclick = handler;
            }
        }
    });

    if (!currentModal.open) {
        currentModal.showModal();
    }
}

function closeModal() {
    if (currentModal) {
        currentModal.close();
        currentModal.remove();
        currentModal = null;
    }
}

/**
 * Point d'entrée principal pour charger un fichier GEDCOM
 * @param {File|string} input - Fichier ou URL à charger
 */
export async function loadGedcomFile(input) {
    logger.gedcom.info("Chargement du fichier:", input);
    
    // Validation des paramètres d'entrée
    if (input === null || input === undefined) {
        console.error("Erreur: input est null ou undefined");
        notificationManager.error("Aucun fichier spécifié pour le chargement.");
        return;
    }
    
    if (isLoadingFile) {
        logger.gedcom.warn("Un chargement de fichier est déjà en cours.");
        return;
    }
    isLoadingFile = true;

    // Émettre l'événement d'import commencé
    logger.gedcom.info('🚀 Émission de l\'événement GEDCOM.IMPORT_STARTED');
    // Standardisation: Ajouter métadonnées d'import
    const importMetadata = {
        type: input instanceof File ? 'direct' : 'cache_restore',
        source: input instanceof File ? 'file_upload' : 'r2_cache', 
        filename: input instanceof File ? input.name : 'cached_data',
        size: input instanceof File ? input.size : null,
        timestamp: new Date().toISOString()
    };
    storeEvents.emit(EVENTS.GEDCOM.IMPORT_STARTED, importMetadata);

    // Afficher l'overlay global pendant le chargement du fichier
    overlayManager.showGlobal("Chargement du fichier GEDCOM...");

    try {
        // Chargement dynamique et initialisation du gestionnaire d'upload
        logger.gedcom.debug("Chargement dynamique du gestionnaire d'upload...");
        const uploadManagerModule = await import('../utils/UploadManager.js');
        uploadManager = uploadManagerModule.default;
        logger.gedcom.info("✅ Gestionnaire d'upload chargé");
        
        // Initialiser le gestionnaire d'upload (assurons-nous que le worker est prêt)
        await uploadManager.initialize();
        logger.gedcom.info("✅ Worker d'upload initialisé");
        
        if (typeof input === 'string') {
            // Chargement d'un fichier distant (URL)
            // Détection cache processed vs GEDCOM classique. Convention :
            //   - `.geneatree` = cache GeneaFan compressé (nouveau format)
            //   - `.lz` = ancien nom du même format (legacy, accepté
            //     pour back-compat le temps que la migration R2 soit
            //     terminée)
            //   - `.json` = cache GeneaFan non compressé (legacy)
            //   - `.ged` / `.gedcom` = source brute à parser
            const isCacheFile =
                input.includes('_processed.json') ||
                /\.(geneatree|lz)(?:\?|$)/i.test(input);
            if (isCacheFile) {
                await loadProcessedCacheFile(input);
            } else {
                loadRemoteGedcomFile(input);
            }
        } else if (input && input.length > 0) {
            // Chargement d'un fichier local
            const file = input[0];
            logger.gedcom.info("Fichier local:", file);
            gedcomFileName = file.name;

            // IMPORTANT: Ne pas écraser le nom temporaire saisi par l'utilisateur
            const currentConfigName = configStore.getConfig.gedcomFileName;
            if (!currentConfigName || !currentConfigName.startsWith('temp_')) {
                configStore.setGedcomFileName(gedcomFileName);
            } else {
                logger.gedcom.info('🔒 Préservation du nom saisi par l\'utilisateur:', currentConfigName);
            }

            uploadManager.setCurrentGedcomFileName(file.name);
            overlayManager.hideGlobal(300);

            // GeneaFan cache files (.geneatree / .lz) are loaded directly
            // via the processed-cache path — no GEDCOM parser, no
            // re-processing. The save modal is skipped because it's
            // hard-wired to the GEDCOM parsing flow; persisting an
            // imported cache to files-service will be a separate UX
            // (Sprint 5+).
            if (/\.(geneatree|lz)$/i.test(file.name)) {
                await loadProcessedCacheFromLocalFile(file);
            } else {
                setTimeout(() => {
                    showSaveFileModal(file, uploadManager);
                }, 300);
            }
        } else {
            // Cas où input est null ou vide - probablement un appel depuis la liste des fichiers
            logger.gedcom.warn("Aucun fichier fourni - redirection vers la liste des fichiers");
            isLoadingFile = false;
            overlayManager.hideGlobal();
            // Ici on pourrait rediriger vers l'affichage de la liste ou gérer autrement
            console.warn("loadGedcomFile appelé sans fichier valide");
            return;
        }
    } catch (error) {
        console.error("Erreur lors de l'initialisation du gestionnaire d'upload:", error);
        isLoadingFile = false;
        overlayManager.hideGlobal();
        notificationManager.error("Erreur lors de l'initialisation. Veuillez réessayer.");
    }
}

/**
 * Charge un cache processed depuis un File local (drag-and-drop ou
 * file picker). Évite GEDCOM parser + re-processing — le cache est
 * déjà au bon format, on décompresse + restore directement.
 */
async function loadProcessedCacheFromLocalFile(file) {
	overlayManager.showGlobal('Chargement du cache GeneaFan local…');
	try {
		const compressedData = await file.text();
		const { default: LZString } = await import('lz-string');
		const decompressedJSON = LZString.decompressFromBase64(compressedData.trim());
		if (!decompressedJSON) {
			throw new Error('Échec de la décompression — le fichier ne semble pas être un .geneatree valide.');
		}
		const processedCache = JSON.parse(decompressedJSON);
		if (!processedCache.metadata || !processedCache.individualsCache) {
			throw new Error('Format de cache invalide (metadata / individualsCache manquants).');
		}

		// Same race-fix as the URL path: setGedcomFileName BEFORE
		// restoreFromProcessedCache so the fan chart's "No GEDCOM file
		// loaded" guard passes when restore fires the root-change event.
		const displayName = file.name
			.replace(/_processed\.geneatree$/, '.ged')
			.replace(/_processed\.lz$/, '.ged')
			.replace(/\.geneatree$/, '.ged')
			.replace(/\.lz$/, '.ged');
		configStore.setGedcomFileName(displayName);
		if (uploadManager) uploadManager.setCurrentGedcomFileName(displayName);

		const { default: processedCacheService } = await import('../utils/processedCacheService.js');
		await processedCacheService.restoreFromProcessedCache(processedCache);

		overlayManager.hideGlobal();
		handleTabsAndOverlay(false);
		logger.gedcom.info(`✅ Cache GeneaFan local chargé : ${processedCache.metadata.individualsCount} individus`);
	} catch (error) {
		overlayManager.hideGlobal();
		console.error('❌ Erreur chargement cache local :', error);
		notificationManager.error(`Impossible de charger ce fichier : ${error.message}`);
	}
}

/**
 * Charge un cache processed depuis une URL distante
 * Optimisation: Sessions personnelles - <200ms au lieu de 5-8s
 * @param {string} url - URL du fichier cache processed (.json ou .lz compressé)
 */
async function loadProcessedCacheFile(url) {
    logger.gedcom.info('🚀 Chargement cache processed depuis URL:', url);
    
    try {
        // Extraire le nom de fichier pour les logs
        const fileName = url.split('/').pop();
        logger.gedcom.info(`📂 Fichier cache: ${fileName}`);
        
        overlayManager.showGlobal('Chargement instantané depuis cache...');
        
        // Chargement du cache processed
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error(`Erreur HTTP ${response.status}: ${response.statusText}`);
        }
        
        // Détecter le format du cache (JSON ou LZ compressé). `.geneatree`
        // (canonical) or `.lz` (legacy) with or without query string for
        // signed R2 URLs are treated as LZ-compressed; .json otherwise.
        let processedCache;

        if (/\.(geneatree|lz)(?:\?|$)/i.test(url)) {
            // Cache compressé avec LZ-String
            const compressedData = await response.text();
            logger.gedcom.debug('📦 Cache compressé détecté, décompression en cours...');
            logger.gedcom.debug('📊 Taille des données compressées:', compressedData.length, 'caractères');
            logger.gedcom.debug('🔍 Début des données:', compressedData.substring(0, 50) + '...');
            
            // Import dynamique de LZ-String pour la décompression
            const { default: LZString } = await import('lz-string');
            logger.gedcom.debug('✅ LZ-String importé');
            
            // Utiliser decompressFromBase64 car les données sont compressées en Base64
            const decompressedJSON = LZString.decompressFromBase64(compressedData);
            logger.gedcom.debug('🔍 Résultat décompression:', decompressedJSON ? 'Succès' : 'Échec');
            
            if (!decompressedJSON) {
                console.error('❌ Données reçues pour debug:', {
                    length: compressedData.length,
                    type: typeof compressedData,
                    firstChars: compressedData.substring(0, 100),
                    isBase64: /^[A-Za-z0-9+/]+=*$/.test(compressedData.trim())
                });
                throw new Error('Échec de la décompression du cache processed');
            }
            
            processedCache = JSON.parse(decompressedJSON);
            logger.gedcom.info('✅ Cache décompressé avec succès');
        } else {
            // Cache JSON non compressé (legacy)
            processedCache = await response.json();
        }
        
        // Validation de base
        if (!processedCache.metadata || !processedCache.individualsCache) {
            throw new Error('Format de cache processed invalide');
        }
        
        logger.gedcom.info(`📊 Cache trouvé: ${processedCache.metadata.individualsCount} individus, version ${processedCache.metadata.version}`);

        // gedcomFileName MUST be set before restoreFromProcessedCache —
        // the restore triggers selectAndSetInitialRoot → root change
        // event → fan chart draw, which throws "No GEDCOM file loaded"
        // if configStore.gedcomFileName is still empty. Strip query
        // string first (signed-URL artifact) for a clean display name.
        const cleanFileName = fileName.split('?')[0];
        gedcomFileName = cleanFileName
            .replace(/_processed\.json$/, '.ged')
            .replace(/_processed\.geneatree$/, '.ged')
            .replace(/_processed\.lz$/, '.ged')
            .replace(/\.geneatree$/, '.ged')
            .replace(/\.lz$/, '.ged');
        configStore.setGedcomFileName(gedcomFileName);
        if (uploadManager) {
            uploadManager.setCurrentGedcomFileName(gedcomFileName);
        }

        // Import du service de cache processed
        const { default: processedCacheService } = await import('../utils/processedCacheService.js');

        // Restauration directe depuis le cache processed
        await processedCacheService.restoreFromProcessedCache(processedCache);
        
        overlayManager.hideGlobal();
        
        // IMPORTANT: Réactiver les onglets après restauration du cache
        handleTabsAndOverlay(false);
        
        logger.gedcom.info('✅ Session restaurée depuis cache processed en <200ms');
        logger.gedcom.info('🎯 Évitement du retraitement GEDCOM (gain 5-8s)');
        
    } catch (error) {
        console.error('❌ Erreur chargement cache processed:', error);
        
        // Fallback automatique vers GEDCOM classique
        logger.gedcom.warn('🔄 Fallback: Tentative chargement GEDCOM classique...');
        
        // Convertir l'URL du cache vers l'URL GEDCOM
        const gedcomUrl = url.replace('_processed.json', '.ged').replace('_processed.lz', '.ged');
        loadRemoteGedcomFile(gedcomUrl);
        
        notificationManager.warning('Cache non disponible, chargement GEDCOM standard...');
    } finally {
        isLoadingFile = false;
    }
}

/**
 * Charge un fichier GEDCOM depuis une URL distante
 * @param {string} url - URL du fichier GEDCOM
 */
function loadRemoteGedcomFile(url) {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "arraybuffer";

    xhr.onload = function (_e) {
        isLoadingFile = false;
        if (this.status === 200) {
            const data = xhr.response;

            // Extraire le nom de fichier de l'URL
            gedcomFileName = url.split("/").pop();
            configStore.setGedcomFileName(gedcomFileName);
            
            // Stocker aussi dans UploadManager pour les générations de cache
            if (uploadManager) {
                uploadManager.setCurrentGedcomFileName(gedcomFileName);
            }

            // Import depuis URL - considéré comme direct car pas d'upload user
            onFileChange(data, { type: 'direct', reason: 'remote_file' });
        } else {
            console.error("Erreur lors du chargement du fichier :", this.status);
            overlayManager.hideGlobal();
            notificationManager.error(__("geneafan.cannot_read_this_file"));
        }
    };

    xhr.onerror = function (e) {
        isLoadingFile = false;
        overlayManager.hideGlobal();
        console.error("Erreur réseau lors du chargement du fichier.", e);
        notificationManager.error(__("geneafan.cannot_read_this_file"));
    };

    xhr.send();
}

/**
 * Affiche la modale demandant si l'utilisateur souhaite sauvegarder le fichier
 * @param {File} file - Fichier GEDCOM
 * @param {Object} uploadManager - Instance du gestionnaire d'upload chargée dynamiquement
 */
function showSaveFileModal(file, uploadManager) {
    showModal('SAVE_REQUEST', {
        '[data-action="no"]': () => {
            closeModal();
            // Passer le contexte d'import direct
            readAndProcessGedcomFile(file, { type: 'direct', reason: 'user_refused' });
        },
        '[data-action="yes"]': () => {
            // Utiliser authStore pour vérifier l'authentification
            authStore.accessFeature(
                // Fonction de callback si l'utilisateur est authentifié
                (userInfo) => {
                    showFamilyNameModal(file, userInfo, uploadManager);
                },
                // Fonction de callback si l'authentification échoue
                () => {
                    logger.gedcom.warn("Authentification requise mais non complétée");
                    closeModal();
                    // Passer le contexte d'import direct
                    readAndProcessGedcomFile(file, { type: 'direct', reason: 'auth_failed' });
                }
            );
        }
    });
}

function showFamilyNameModal(file, userInfo, uploadManager) {
    showModal('FAMILY_NAME', {
        'form': (_event) => {
            const familyName = document.getElementById('familyNameInput').value.trim();
            if (familyName) {
                // Commencer immédiatement le traitement du fichier
                closeModal();
                
                // ✅ SUPPRIMÉ: Stockage nom de famille - plus utilisé avec read-gedcom-geneafan
                logger.gedcom.info('👤 Nom de famille saisi:', familyName);
                
                // Écouter l'événement de cache processed prêt pour l'upload
                const unsubscribe = storeEvents.subscribe(EVENTS.CACHE.PROCESSED_READY, async (processedCache) => {
                    logger.gedcom.info('📤 Cache processed prêt, upload via files-service avec nom:', familyName);
                    try {
                        await uploadProcessedCacheToFilesService(processedCache, familyName);
                        logger.gedcom.info('✅ Upload cache terminé avec succès (files-service)');
                    } catch (error) {
                        console.error('❌ Erreur upload cache via files-service:', error);
                        notificationManager.error('Échec de la sauvegarde dans le cloud');
                    }
                    unsubscribe(); // Se désabonner après usage
                });
                
                // Passer le contexte d'import avec upload voulu
                readAndProcessGedcomFile(file, { type: 'upload', reason: 'user_requested' });
                
                // Lancer l'upload GEDCOM en arrière-plan
                uploadGedcomFileInBackground(file, familyName, userInfo);
            } else {
                notificationManager.warning('Veuillez entrer le nom de la famille.');
            }
        }
    });
}

// Nouvelle fonction pour gérer l'upload en arrière-plan
function uploadGedcomFileInBackground(file, familyName, userInfo) {
    const userId = userInfo.id;
    if (!userId) {
        // Utiliser le gestionnaire existant
        notificationManager.error('Impossible de récupérer votre identifiant utilisateur.');
        return;
    }

    logger.gedcom.debug('📋 Upload GEDCOM source désactivé - seul le cache processed sera uploadé');
    // Stocker directement le nom du fichier local pour le cache processed
    const processedFileName = `${familyName}_processed.geneatree`;
    logger.gedcom.debug('🔧 Configuration du nom de fichier pour le cache processed:', processedFileName);
    configStore.setGedcomFileName(processedFileName);
}

/**
 * Lit et traite le fichier GEDCOM
 * @param {File} file - Fichier GEDCOM à traiter
 * @param {Object} importContext - Contexte d'import avec type et reason
 */
function readAndProcessGedcomFile(file, importContext = null) {
    logger.gedcom.info('Reading and processing file:', file);
    isLoadingFile = true;
    
    // Afficher l'overlay global avec un message explicite
    overlayManager.showGlobal("Lecture du fichier GEDCOM...");
    
    const reader = new FileReader();

    reader.addEventListener("loadend", function () {
        isLoadingFile = false;
        const data = reader.result;

        // Continuer avec la logique de l'application
        onFileChange(data, importContext);
    });

    reader.readAsArrayBuffer(file);
}

/**
 * Recherche l'individu le plus jeune dans la liste des individus
 * 🚀 OPTIMISÉ: Accès direct au cache pour éviter les créations de Proxy sur chaque individu
 * @param {Array} individualIds - Liste des IDs des individus
 * @returns {Object|null} - L'individu le plus jeune ou null si aucun n'est trouvé
 */
function findYoungestIndividual(individualIds) {
    const findYoungestStart = Date.now();
    const isLargeDataset = individualIds.length > 10000;

    if (isLargeDataset) {
        logger.gedcom.info(`⚡ findYoungestIndividual: Mode optimisé pour ${individualIds.length} individus`);
    }

    // 🚀 OPTIMISATION: Accès direct au cache pour les gros datasets
    const rawCache = gedcomDataStore.individualsCache;
    let youngest = null;
    let youngestTimestamp = 0;
    let processedCount = 0;
    let validDatesCount = 0;

    for (const individualId of individualIds) {
        const individual = rawCache.get(individualId);
        if (!individual) continue;
        processedCount++;

        // 🚀 Accès direct aux propriétés compressées (pas de Proxy, pas d'appel de fonction)
        // Priorité: birthYear direct > extraction depuis birthDate > extraction depuis événements
        let birthYear = individual.birthYear;
        let birthDate = individual.birthDate;

        // Si pas de birthYear, essayer d'extraire depuis birthDate
        if (!birthYear && birthDate) {
            if (typeof birthDate === 'string' && birthDate.includes('/')) {
                const parts = birthDate.split('/');
                birthYear = parts[2]; // Format DD/MM/YYYY
            } else if (!isNaN(birthDate)) {
                birthYear = String(birthDate);
            }
        }

        // Si toujours pas de birthYear, essayer depuis les événements compressés
        if (!birthYear && individual.e && Array.isArray(individual.e)) {
            const birthEvent = individual.e.find(e => e.t === 'fb' || e.type === 'birth');
            if (birthEvent && birthEvent.d) {
                // Date compressée format YYYYMMDD (number)
                if (typeof birthEvent.d === 'number') {
                    birthYear = String(birthEvent.d).substring(0, 4);
                } else if (typeof birthEvent.d === 'string') {
                    // Format DD/MM/YYYY ou YYYY
                    if (birthEvent.d.includes('/')) {
                        birthYear = birthEvent.d.split('/')[2];
                    } else if (birthEvent.d.length >= 4) {
                        birthYear = birthEvent.d.substring(0, 4);
                    }
                }
            }
        }

        if (!birthYear) continue;

        const yearNum = parseInt(birthYear, 10);
        if (isNaN(yearNum) || yearNum < 1000 || yearNum > 2100) continue;

        validDatesCount++;

        // Créer un timestamp approximatif basé sur l'année (et mois/jour si disponibles)
        let timestamp = yearNum * 10000; // Base: année * 10000

        // Bonus pour date complète si disponible
        if (birthDate && typeof birthDate === 'string' && birthDate.includes('/')) {
            const parts = birthDate.split('/');
            const month = parseInt(parts[1], 10) || 1;
            const day = parseInt(parts[0], 10) || 1;
            timestamp = yearNum * 10000 + month * 100 + day;
        }

        if (timestamp > youngestTimestamp) {
            youngestTimestamp = timestamp;
            youngest = { id: individualId, birthYear: yearNum };
        }
    }

    const findYoungestTime = Date.now() - findYoungestStart;
    logger.gedcom.info(`⚡ findYoungestIndividual: ${processedCount} traités, ${validDatesCount} avec dates valides en ${findYoungestTime}ms`);

    if (youngest) {
        // Log minimal pour les gros datasets
        if (isLargeDataset) {
            const ind = rawCache.get(youngest.id);
            let name = '', surname = '';
            if (ind) {
                if (ind.fn) {
                    const parts = ind.fn.split('|');
                    surname = parts[0] || '';
                    name = parts[1] || '';
                } else {
                    name = ind.name || '';
                    surname = ind.surname || '';
                }
            }
            logger.gedcom.info(`🎯 Plus jeune: ${surname} ${name} (${youngest.id}) - Année: ${youngest.birthYear}`);
        } else {
            // Debug complet pour les petits datasets
            const youngestIndividual = gedcomDataStore.getIndividual(youngest.id);
            const name = getName(youngestIndividual);
            const surname = getSurname(youngestIndividual);
            logger.gedcom.debug(`🎯 Personne sélectionnée: ${name} ${surname} (${youngest.id}) - Année: ${youngest.birthYear}`);
        }
    }

    return youngest;
}


/**
 * Gère le traitement du fichier GEDCOM après son chargement
 * @param {ArrayBuffer} data - Données du fichier GEDCOM
 */
export async function onFileChange(data, importContext = null) {
    // Utiliser l'overlay global pour indiquer le début du traitement
    overlayManager.showGlobal('Lecture du fichier GEDCOM...');
    
    // Informer les autres composants du début du traitement
    // Standardisation: Métadonnées de processus
    const processStartTime = Date.now();
    storeEvents.emit(EVENTS.PROCESS.START, {
        step: 'file_reading',
        message: 'Lecture du fichier GEDCOM...',
        timestamp: new Date().toISOString(),
        processId: `gedcom_${processStartTime}`
    });
    
    // Désactiver les onglets et ajuster l'UI
    handleTabsAndOverlay(true);
    
    // Réinitialisation complète et centralisée
    await resetBeforeNewGedcom();
    
    // ✅ SUPPRIMÉ: Réinitialisation nom de famille - plus nécessaire

    // Émettre l'événement d'import APRÈS le reset pour éviter que les flags soient effacés
    if (importContext) {
        logger.gedcom.debug('🔄 Émission événement import:', importContext);
        storeEvents.emit(EVENTS.IMPORT.STARTED, importContext);
    } else {
        logger.gedcom.warn('⚠️ Aucun contexte d\'import fourni - traitement comme import direct');
        storeEvents.emit(EVENTS.IMPORT.STARTED, { type: 'direct', reason: 'unknown_source' });
    }

    // Variable pour mesurer le temps de transfert des caches
    let cacheTransferStart;
    
    try {
        gedcomDataStore.setFileUploaded(true);

        // Mise à jour du message de l'overlay
        overlayManager.showGlobal('📖 Analyse de la structure GEDCOM...');
        storeEvents.emit(EVENTS.PROCESS.START, {
            step: 'file_parsing',
            message: 'Analyse de la structure GEDCOM...',
            timestamp: new Date().toISOString(),
            processId: `gedcom_${processStartTime}`
        });
        
        // 🚀 ARCHITECTURE SOLIDE: Utilisation unique de @fbracq/read-gedcom-geneafan
        logger.gedcom.info("🚀 Parsing GEDCOM avec @fbracq/read-gedcom-geneafan...");

        // ⏱️ PERFORMANCE: Mesure détaillée du temps de parsing
        const importStartTime = Date.now();
        const { parseGedcomToGeneaFan } = await import("@fbracq/read-gedcom-geneafan");
        const importTime = Date.now() - importStartTime;
        logger.gedcom.info(`⚡ Import module: ${importTime}ms`);

        // Estimation de la taille pour adapter l'overlay
        const dataSizeMB = (data.byteLength / 1024 / 1024).toFixed(2);
        logger.gedcom.info(`📂 Taille du fichier: ${dataSizeMB} MB`);

        // Mise à jour de l'overlay
        overlayManager.showGlobal(`📂 Lecture du fichier (${dataSizeMB} MB)...`);

        const gedcomProcessingStartTime = Date.now();

        // 🚀 OPTIMISATION: Pour les gros fichiers, activer le mode verbose pour voir la progression
        const isLargeFile = data.byteLength > 10 * 1024 * 1024; // > 10 MB
        if (isLargeFile) {
            logger.gedcom.info(`⚠️ Gros fichier détecté (${dataSizeMB} MB) - Mode progression activé`);
        }

        // 🔴 DIAGNOSTIC: Log AVANT le parsing pour mesurer le temps exact
        console.log('🔴 DÉBUT parseGedcomToGeneaFan - UI va être bloquée pendant le parsing...');
        console.log(`🔴 Taille: ${dataSizeMB} MB - Estimation: plusieurs minutes pour 100K+ individus`);

        const result = await parseGedcomToGeneaFan(data, {
            verbose: isLargeFile,     // 🚀 Activer pour gros fichiers pour voir progression
            fixEncoding: true,
            logPlaces: false,         // 🔴 DÉSACTIVÉ pour performance
            onProgress: isLargeFile ? (progress) => {
                // Mettre à jour l'overlay avec la progression
                if (progress.phase === 'individuals') {
                    overlayManager.showGlobal(
                        `👥 Extraction des personnes: ${progress.current.toLocaleString()} / ${progress.total.toLocaleString()} (${progress.percent}%)`
                    );
                } else if (progress.phase === 'families') {
                    overlayManager.showGlobal(
                        `👨‍👩‍👧 Analyse des familles: ${progress.current.toLocaleString()} / ${progress.total.toLocaleString()} (${progress.percent}%)`
                    );
                } else if (progress.phase === 'places') {
                    overlayManager.showGlobal(
                        `🗺️ Identification des lieux: ${progress.current.toLocaleString()} lieu(x) trouvé(s)`
                    );
                }
            } : undefined
        });
        const parseTime = Date.now() - gedcomProcessingStartTime;

        // 🔴 DIAGNOSTIC: Log APRÈS le parsing
        console.log(`✅ FIN parseGedcomToGeneaFan - Durée totale: ${(parseTime/1000).toFixed(1)}s`);

        logger.gedcom.info(`✅ @fbracq/read-gedcom-geneafan: ${result.individualsCache.size} individus parsés en ${parseTime}ms (${(parseTime/1000).toFixed(1)}s)`);
        logger.gedcom.info("🔤 Encodage ANSI automatiquement corrigé");
        
        // DEBUG: Vérifier la structure du résultat
        logger.gedcom.debug("🔍 Structure du résultat:", {
            individualsCache: result.individualsCache?.size,
            familiesCache: result.familiesCache?.size,
            notesCache: result.notesCache?.size, // 🆕 Vérifier la présence du notesCache
            sourcesCache: result.sourcesCache?.size, // 🆕 Vérifier la présence du sourcesCache
            mediaCache: result.mediaCache?.size, // 🆕 Vérifier la présence du mediaCache
            familyTownsStore: result.familyTownsStore ? Object.keys(result.familyTownsStore).length : 0,
            places: result.places?.size,
            statistics: !!result.statistics,
            metadata: !!result.metadata
        });
        
        // 🏠 Log familiesCache pour validation
        if (result.familiesCache && result.familiesCache.size > 0) {
            logger.gedcom.debug(`🏠 FAMILIES CACHE: ${result.familiesCache.size} familles avec événements`);
            // Afficher les 3 premières familles pour debug
            let count = 0;
            for (const [id, family] of result.familiesCache.entries()) {
                if (count >= 3) break;
                logger.gedcom.debug(`${id}:`, family);
                count++;
            }
        }
        
        // Le résultat contient déjà les caches optimisées avec relations parentales
        const json = result;

        // Mise à jour du message pour le géocodage
        overlayManager.showGlobal(`✅ ${result.individualsCache.size.toLocaleString()} personnes extraites en ${(parseTime/1000).toFixed(1)}s`);

        // Pause courte pour afficher le message de succès
        await new Promise(resolve => setTimeout(resolve, 500));

        overlayManager.showGlobal('🗺️ Préparation des données géographiques...');
        storeEvents.emit(EVENTS.PROCESS.START, {
            step: 'geocoding',
            message: 'Préparation des données géographiques...',
            timestamp: new Date().toISOString(),
            processId: `gedcom_${processStartTime}`
        });
        
        // 🚀 ARCHITECTURE OPTIMISÉE: Utiliser directement read-gedcom-geneafan v0.1.4 avec normalisation corrigée
        // 🎯 read-gedcom-geneafan gère tout maintenant !
        logger.gedcom.info(`✅ Lieux traités par read-gedcom-geneafan: ${json.places?.size || 0} lieux avec normalisation sophistiquée`);
        const sourceData = json; // Utiliser directement les données optimisées

        // Mise à jour du message pour la construction des données
        overlayManager.showGlobal('💾 Organisation des données généalogiques...');
        await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir
        storeEvents.emit(EVENTS.PROCESS.START, {
            step: 'data_building',
            message: 'Organisation des données généalogiques...',
            timestamp: new Date().toISOString(),
            processId: `gedcom_${processStartTime}`
        });

        // 🚀 ARCHITECTURE SOLIDE: Transférer les caches optimisées vers gedcomDataStore
        logger.gedcom.info("📋 Transfert des caches optimisées...");
        cacheTransferStart = Date.now();

        // Transférer le cache des individus avec relations parentales
        if (sourceData.individualsCache && sourceData.individualsCache.size > 0) {
            overlayManager.showGlobal(`👥 Enregistrement de ${sourceData.individualsCache.size.toLocaleString()} personnes...`);
            await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir
            logger.gedcom.info(`📥 Transfert de ${sourceData.individualsCache.size} individus vers gedcomDataStore`);
            gedcomDataStore.setIndividualsCache(sourceData.individualsCache);

            // ✅ Log de cache supprimé - utiliser saveCache() dans la console
        }

        // 🏠 Transférer le cache des familles
        if (sourceData.familiesCache && sourceData.familiesCache.size > 0) {
            overlayManager.showGlobal(`👨‍👩‍👧 Enregistrement de ${sourceData.familiesCache.size.toLocaleString()} familles...`);
            await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir
            logger.gedcom.info(`🏠 Transfert de ${sourceData.familiesCache.size} familles vers gedcomDataStore`);
            gedcomDataStore.setFamiliesCache(sourceData.familiesCache);
        }

        // 🗺️ Transfert familyTownsStore - Log supprimé, utiliser saveFamilyTowns() dans la console
        if (sourceData.familyTownsStore && Object.keys(sourceData.familyTownsStore).length > 0) {
            const townCount = Object.keys(sourceData.familyTownsStore).length;
            overlayManager.showGlobal(`🗺️ Enregistrement de ${townCount.toLocaleString()} lieu(x)...`);
            await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir
            familyTownsStore.importFamilyTowns(sourceData.familyTownsStore);
        }

        // 🔗 Agréger les événements (naissance/décès/mariage) des individus dans
        // familyTownsStore.town.events : c'est ce qui alimente le compte affiché sur
        // les marqueurs, le calque Patronymes et les InfoWindows du calque Famille.
        // Sur le chemin de RESTAURATION de cache, c'est restoreFromProcessedCache qui
        // émet BULK_ADDED ; sur un IMPORT FRAIS (.ged parsé), il faut l'émettre ici —
        // sinon town.events reste vide et tout affiche 0 (bug "undefined"/0 historique).
        // Émis après importFamilyTowns pour que les villes (et leurs coords) existent déjà.
        if (sourceData.individualsCache && sourceData.individualsCache.size > 0) {
            // Le handler BULK_ADDED itère `individuals.forEach(([id, individual]) => …)`,
            // donc il attend un TABLEAU d'entrées [id, individual] (comme le cache
            // restauré le fournit), pas une Map — d'où la conversion.
            storeEvents.emit(EVENTS.INDIVIDUALS.BULK_ADDED, [...sourceData.individualsCache]);
        }

        // 📝 NotesCache - Stocké dans gedcomDataStore pour éviter les variables globales
        logger.gedcom.debug("🔍 Debug NotesCache - Présence:", !!sourceData.notesCache, "Taille:", sourceData.notesCache?.size);
        if (sourceData.notesCache && sourceData.notesCache.size > 0) {
            logger.gedcom.debug("📝 Stockage de", sourceData.notesCache.size, "notes dans gedcomDataStore");
            // Afficher quelques exemples de notes
            let noteCount = 0;
            for (const [id, note] of sourceData.notesCache.entries()) {
                if (noteCount >= 3) break;
                logger.gedcom.debug(`  - ${id}:`, note.text?.substring(0, 100) + "...");
                noteCount++;
            }
            gedcomDataStore.setNotesCache(sourceData.notesCache);
            logger.gedcom.debug("✅ Notes stockées. Vérification:", gedcomDataStore.notesCache.size, "notes dans le store");
        } else {
            logger.gedcom.warn("⚠️ Aucune note trouvée dans sourceData.notesCache");
        }
        
        // 📷 Media Cache - Stocké dans gedcomDataStore
        if (sourceData.mediaCache && sourceData.mediaCache.size > 0) {
            gedcomDataStore.setMediasCache(sourceData.mediaCache);
            logger.gedcom.debug(`\n📷 MEDIA CACHE (${sourceData.mediaCache.size} entrées, tous affichés):`);
            for (const [id, media] of sourceData.mediaCache.entries()) {
                logger.gedcom.debug(`${id}:`, JSON.stringify(media, null, 2));
            }
        }
        
        // 📚 Sources Cache - Stocké dans gedcomDataStore
        if (sourceData.sourcesCache && sourceData.sourcesCache.size > 0) {
            gedcomDataStore.setSourcesCache(sourceData.sourcesCache);
            logger.gedcom.debug(`\n📚 SOURCES CACHE (${sourceData.sourcesCache.size} entrées, premiers 10):`);
            let count = 0;
            for (const [id, source] of sourceData.sourcesCache.entries()) {
                if (count >= 10) break;
                logger.gedcom.debug(`${id}:`, JSON.stringify(source, null, 2));
                count++;
            }
        }
        
        console.groupEnd();
        
        // 🚀 Déclencher géocodage en tâche de fond pour enrichissement
        setTimeout(async () => {
            try {
                logger.gedcom.info('🔄 Vérification des villes nécessitant un géocodage...');

                // Compter les villes nécessitant un géocodage
                const townsToGeocode = familyTownsStore._collectTownsNeedingUpdate();
                const townCount = Object.keys(townsToGeocode).length;

                logger.gedcom.info(`📊 Analyse: ${familyTownsStore.townsData.size} villes au total, ${townCount} nécessitent un géocodage`);
                if (townCount > 0) {
                    logger.gedcom.debug('🗺️ Villes à géocoder:', Object.keys(townsToGeocode));
                }

                if (townCount > 0) {
                    // Désactiver l'onglet Map pendant le géocodage
                    handleMapTabDuringGeocoding(true);

                    await familyTownsStore.updateTownsViaProxy();

                    // Réactiver l'onglet Map
                    handleMapTabDuringGeocoding(false);

                    logger.gedcom.info('✅ Géocodage en arrière-plan terminé');
                } else {
                    logger.gedcom.info('✅ Aucun géocodage nécessaire - toutes les villes sont déjà enrichies avec coordonnées et départements');
                }
            } catch (error) {
                console.warn('⚠️ Erreur géocodage en arrière-plan:', error);

                // Réactiver l'onglet Map en cas d'erreur
                handleMapTabDuringGeocoding(false);

                if (error.code === 'TOO_MANY_TOWNS') {
                    logger.gedcom.warn(`🚫 Géocodage désactivé: ${error.townCount} villes > limite ${error.limit}`);
                }
            }
        }, 1000); // Délai pour permettre l'affichage rapide de l'éventail
        
        // Note: read-gedcom-geneafan n'utilise pas de familiesCache séparé
        // Les relations familiales sont directement intégrées dans individualsCache (architecture optimisée)
        
        const cacheTransferTime = Date.now() - cacheTransferStart;
        logger.gedcom.info(`✅ Caches transférées: ${gedcomDataStore.individualsCache.size} individus en ${cacheTransferTime}ms`);
        
        // 📊 DURÉE TOTALE du traitement GEDCOM complet
        const totalProcessingTime = Date.now() - gedcomProcessingStartTime;
        logger.gedcom.info(`🏁 TRAITEMENT GEDCOM COMPLET: ${totalProcessingTime}ms (parsing → caches enregistrées dans GeneaFan)`);

        // Initialiser ou réinitialiser Virtual Select
        // On réinitialise toujours car initializeVirtualSelect() gère la destruction/recréation
        rootPersonStore.initializeVirtualSelect();

        // 🚀 OPTIMISATION: Construire l'index de recherche pré-calculé
        // Cela permet des recherches O(1) au lieu de O(n) dans le cache
        rootPersonStore.buildSearchIndex();

        // 🚀 OPTIMISATION: Chargement asynchrone - ne pas pré-charger les options
        // Virtual Select utilise maintenant onServerSearch() pour rechercher à la demande
        const individuals = Array.from(gedcomDataStore.individualsCache.keys());
        const individualsCount = individuals.length;
        logger.gedcom.info(`✅ ${individualsCount} individus disponibles pour la recherche (chargement asynchrone)`);

        // Afficher le bouton info GEDCOM avec tooltip
        updateGedcomInfoButton(individualsCount);

        // Finalisation
        overlayManager.showGlobal('🎯 Préparation de la sélection des personnes...');
        await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir
        storeEvents.emit(EVENTS.PROCESS.START, {
            step: 'finalization',
            message: 'Préparation de la sélection des personnes...',
            timestamp: new Date().toISOString(),
            processId: `gedcom_${processStartTime}`
        });

        // ✅ SUPPRIMÉ: updateIndividualTownsFromFamilyTowns - obsolète avec read-gedcom-geneafan
        // Les événements individuels contiennent déjà les townKey, familyTownsStore est peuplé séparément

        logger.gedcom.debug("Individuals cache updated:", gedcomDataStore.getIndividualsCache());

        // Initialiser les statistiques
        overlayManager.showGlobal('📊 Calcul des statistiques familiales...');
        await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir
        logger.gedcom.info('Initializing statistics service...');
        try {
            const { statisticsService } = await import('../tabs/statistics/services/statisticsService.js');
            await statisticsService.initialize();
            logger.gedcom.debug('Processing family data...');
            await statisticsService.processFamilyData();
        } catch (error) {
            console.error('Failed to initialize or process statistics:', error);
            // Continue with other processing even if statistics fails
        }

        // Sélection de la personne racine
        overlayManager.showGlobal('🎯 Sélection de la personne racine...');
        await new Promise(resolve => setTimeout(resolve, 100)); // Permettre à l'UI de se rafraîchir

        let rootId;
        const gedcomFileName = configStore.getConfig.gedcomFileName;
        if (gedcomFileName === "Arbre_Robin_Keller_7G_25-10-2023.ged") {
            rootId = "@I124@"; //@I111@";
        } else {
            const youngestPerson = findYoungestIndividual(individuals);
            rootId = youngestPerson ? youngestPerson.id : individuals[0];
        }

        // Mise à jour du root et du nom
        if (rootId) {
            // Récupérer l'individu le plus jeune pour définir comme racine
            const rootPerson = gedcomDataStore.getIndividual(rootId);
            if (rootPerson) {
                // 🚀 OPTIMISATION: Ajouter uniquement l'option de la personne racine
                // Virtual Select utilise maintenant onServerSearch() pour les autres recherches
                const name = getName(rootPerson) || '';
                const surname = getSurname(rootPerson) || '';
                const firstName = name.split(' ')[0] || name;
                const shortName = `${surname} ${firstName} (${rootPerson.birthYear || '?'}${rootPerson.deathYear ? '-' + rootPerson.deathYear : ''})`;

                // Définir l'option initiale et la valeur pour Virtual Select
                rootPersonStore.setVirtualSelectValue(rootId, shortName);

                rootPersonStore.setRoot(rootId, { skipDraw: false });
                rootPersonStore.setRootPersonName({
                    name: rootPerson.name,
                    surname: rootPerson.surname,
                });
            }
        }

        // Traitement terminé avec succès - Standardisation avec métadonnées
        const processDuration = Date.now() - processStartTime;

        // 📊 RÉSUMÉ PERFORMANCE: Afficher les temps de chaque étape
        const performanceSummary = {
            totalDuration: `${(processDuration/1000).toFixed(1)}s`,
            parsing: `${(parseTime/1000).toFixed(1)}s`,
            cacheTransfer: `${cacheTransferTime}ms`,
            individualsCount: gedcomDataStore.totalIndividuals,
            familiesCount: gedcomDataStore.totalFamilies
        };
        logger.gedcom.info('📊 ═══════════════════════════════════════════════════════════');
        logger.gedcom.info(`📊 RÉSUMÉ PERFORMANCE - ${gedcomDataStore.totalIndividuals} individus`);
        logger.gedcom.info(`📊 ─────────────────────────────────────────────────────────────`);
        logger.gedcom.info(`📊 ⏱️  TOTAL:           ${performanceSummary.totalDuration}`);
        logger.gedcom.info(`📊 📖 Parsing GEDCOM:  ${performanceSummary.parsing}`);
        logger.gedcom.info(`📊 💾 Transfert cache: ${performanceSummary.cacheTransfer}`);
        logger.gedcom.info(`📊 👥 Individus:       ${performanceSummary.individualsCount}`);
        logger.gedcom.info(`📊 👨‍👩‍👧 Familles:         ${performanceSummary.familiesCount}`);
        logger.gedcom.info(`📊 🚀 Dropdown:        Chargement asynchrone activé`);
        logger.gedcom.info('📊 ═══════════════════════════════════════════════════════════');

        storeEvents.emit(EVENTS.PROCESS.COMPLETE, {
            step: 'completed',
            message: 'Traitement GEDCOM terminé avec succès',
            duration: processDuration,
            timestamp: new Date().toISOString(),
            processId: `gedcom_${processStartTime}`,
            individualsCount: gedcomDataStore.totalIndividuals,
            performanceSummary
        });

        // Émettre CACHE.BUILT pour déclencher la génération du cache processed
        const cacheSize = gedcomDataStore.individualsCache.size * 1024; // Estimation
        storeEvents.emit(EVENTS.CACHE.BUILT, {
            source: 'gedcom_import',
            individualsCount: gedcomDataStore.totalIndividuals,
            cacheSize: cacheSize,
            cacheSizeMB: (cacheSize / 1024 / 1024).toFixed(2),
            isDirectImport: true,
            isRestoringFromCache: false,
            timestamp: new Date().toISOString(),
            cacheVersion: '1.0'
        });

        // Programmer la génération du cache processed en arrière-plan
        // Ceci émettra EVENTS.CACHE.PROCESSED_READY pour l'upload
        gedcomDataStore.scheduleProcessedCacheGeneration();

        // Afficher message de succès final
        overlayManager.showGlobal(`✅ Import terminé ! ${gedcomDataStore.totalIndividuals.toLocaleString()} personnes chargées`);

        // Pause courte pour afficher le message de succès
        await new Promise(resolve => setTimeout(resolve, 800));

        // Masquer l'overlay global
        overlayManager.hideGlobal();

    } catch (error) {
        console.error("General Error:", error);
        const processDuration = Date.now() - processStartTime;
        storeEvents.emit(EVENTS.PROCESS.ERROR, {
            step: 'error',
            error: error.message || 'Erreur inconnue',
            duration: processDuration,
            timestamp: new Date().toISOString(),
            processId: `gedcom_${processStartTime}`,
            stack: error.stack
        });
        
        // En cas d'erreur, masquer l'overlay et afficher un message d'erreur
        overlayManager.hideGlobal();
        notificationManager.error("Une erreur est survenue lors du traitement du fichier GEDCOM.");
    } finally {
        handleTabsAndOverlay(false);
        // ✅ SUPPRIMÉ: setupPersonLinkEventListener() - PersonDetailsManager gère automatiquement

        // Re-ajouter les event listeners pour les paramètres
        const parametersElements = document.querySelectorAll(".parameter");
        const individualSelectElement = document.getElementById("individual-select");
        [...parametersElements, individualSelectElement].forEach((element) => {
            if (element) {
                element.addEventListener("change", configStore.handleSettingChange);
            }
        });
    }
}

/**
 * Gère l'affichage/masquage des onglets via le store MobX centralisé
 * @param {boolean} shouldShowLoading - Indique si l'état de chargement doit être affiché
 */
function handleTabsAndOverlay(shouldShowLoading) {
    // Import dynamique du store pour éviter les dépendances circulaires
    import('../common/stores/tabStateStore.js').then(({ tabStateStore }) => {
        if (shouldShowLoading) {
            // Désactiver tous les onglets sauf Éventail
            tabStateStore.setProcessing(true);
            tabStateStore.disableAllTabsExceptFan();
            
            // Force l'affichage de tab1
            const tab1Link = document.querySelector('#tab-nav a[href="#tab1"]');
            if (tab1Link) tab1Link.click();
        } else {
            // Réactiver tous les onglets
            tabStateStore.setProcessing(false);
            tabStateStore.enableAllTabs();
        }
    }).catch(error => {
        console.error('❌ Erreur lors du chargement du tabStateStore:', error);
        // Fallback vers l'ancienne méthode si le store n'est pas disponible
        handleTabsAndOverlayFallback(shouldShowLoading);
    });
}

/**
 * Méthode fallback pour la compatibilité (au cas où le store MobX ne serait pas disponible)
 */
function handleTabsAndOverlayFallback(shouldShowLoading) {
    const tabsToDisable = ["tab2", "tab3", "tab4", "tab5", "tab6", "tab7", "tab8"];
    tabsToDisable.forEach(tabId => {
        const tabLink = document.querySelector(`#tab-nav a[href="#${tabId}"]`);
        if (tabLink) {
            tabLink.classList.toggle('disabled', shouldShowLoading);
            tabLink.setAttribute('aria-disabled', shouldShowLoading ? 'true' : 'false');
            tabLink.setAttribute('tabindex', shouldShowLoading ? '-1' : '0');
        }
    });
    
    if (shouldShowLoading) {
        const tab1Link = document.querySelector('#tab-nav a[href="#tab1"]');
        if (tab1Link) tab1Link.click();
    }
}

/**
 * Gère l'état de l'onglet Map pendant le géocodage via le store MobX
 * @param {boolean} isGeocoding - true si géocodage en cours, false sinon
 */
function handleMapTabDuringGeocoding(isGeocoding) {
    // Import dynamique du store pour éviter les dépendances circulaires
    import('../common/stores/tabStateStore.js').then(({ tabStateStore }) => {
        if (isGeocoding) {
            // Désactiver l'onglet Map via le store
            tabStateStore.setGeocoding(true);
            tabStateStore.setTabEnabled('tab2', false);
        } else {
            // Réactiver l'onglet Map via le store
            tabStateStore.setGeocoding(false);
            // Le store gère automatiquement la réactivation selon l'état de processing
            if (!tabStateStore.isProcessing) {
                tabStateStore.setTabEnabled('tab2', true);
            }
        }
    }).catch(error => {
        console.error('❌ Erreur lors du chargement du tabStateStore pour géocodage:', error);
        // Fallback vers l'ancienne méthode
        handleMapTabDuringGeocodingFallback(isGeocoding);
    });
}

/**
 * Méthode fallback pour le géocodage (compatibilité)
 */
function handleMapTabDuringGeocodingFallback(isGeocoding) {
    const mapTabLink = document.querySelector('#tab-nav a[href="#tab2"]');
    if (mapTabLink) {
        if (isGeocoding) {
            mapTabLink.classList.add('disabled');
            mapTabLink.setAttribute('aria-disabled', 'true');
            mapTabLink.setAttribute('tabindex', '-1');
            mapTabLink.setAttribute('title', 'Géolocalisation en cours...');
        } else {
            mapTabLink.classList.remove('disabled');
            mapTabLink.setAttribute('aria-disabled', 'false');
            mapTabLink.setAttribute('tabindex', '0');
            mapTabLink.removeAttribute('title');
        }
    }
}

/**
 * Récupère la liste des fichiers GEDCOM de l'utilisateur via files-service
 * (architecture D1 + R2, remplace user-file-access KV). Les fichiers sont
 * filtrés par metadata.kind='gedcom' côté backend ; le SPA n'a plus à
 * filtrer sur le suffixe _processed.lz.
 *
 * @returns {Promise<Array<{id: string, name: string, status: string, owner_id: string}>>}
 *   status = 'owned' si le caller est l'uploader, 'shared' sinon.
 */
export async function fetchUserGedcomFiles() {
    if (!authStore.isAuthenticated) {
        logger.gedcom.warn('Aucun utilisateur authentifié pour récupérer les fichiers');
        return [];
    }

    try {
        const { filesService } = await import('../services/filesService.js');
        // Always include superseded entries — the SPA badges originals vs
        // merged versions so the user can see the full chain. Without
        // this, deleting a merged child makes the parent silently reappear
        // (looks like a phantom resurrect of the deleted file).
        const data = await filesService.listFiles({ kind: 'gedcom', limit: 200, includeSuperseded: true });
        const currentEmail = authStore.userInfo?.email || '';
        const files = (data?.files || []).map((f) => ({
            id: f.id,
            name: f.name,
            familyName: f.metadata?.familyName || null,
            owner_id: f.owner_id,
            status: f.owner_id === currentEmail ? 'owned' : 'shared',
            parentFileId: f.parent_file_id || null,
            // The contributor (B side) absorbed during a fusion — pulled
            // from the mergeHistory the worker stores at upload time.
            // Null for non-merged files. Quoted in the dropdown tooltip.
            contributorFileId: f.metadata?.mergeHistory?.contributorFileId || null,
            hasChildren: f.has_children === true,
            uploadedAt: f.uploaded_at || null,
        }));
        logger.gedcom.info(`📋 files-service: ${files.length} GEDCOM(s) visibles`);
        return files;
    } catch (error) {
        console.error('Erreur lors de la récupération des fichiers GEDCOM via files-service :', error);
        return [];
    }
}

/**
 * Upload le processed cache (LZ-String compressé) directement vers
 * files-service via /files-api/files. Remplace l'ancien pipeline
 * UploadManager → cloudflare-r2-uploader → user-file-access-worker.
 *
 * files-service crée automatiquement un primary group pour le fichier
 * via groups-service, ce qui rend la GEDCOM partageable nativement.
 *
 * @param {string} compressedCache - Cache processed compressé en base64
 * @param {string} familyName - Nom de famille saisi par l'utilisateur
 * @returns {Promise<Object>} - Entry retournée par files-service
 */
async function uploadProcessedCacheToFilesService(compressedCache, familyName) {
    if (!compressedCache || typeof compressedCache !== 'string') {
        throw new Error('Cache processed compressé invalide');
    }
    const safeName = (familyName || 'unknown').replace(/[^a-zA-Z0-9._-]/g, '_');
    const uploadDate = new Date().toISOString().split('T')[0];
    const fileName = `${safeName}_date_${uploadDate}_processed.geneatree`;

    const blob = new Blob([compressedCache], { type: 'text/plain' });
    const file = new File([blob], fileName, { type: 'text/plain' });

    logger.gedcom.info(`📤 Upload ${fileName} (${(file.size / 1024).toFixed(1)} KB) vers files-service`);

    const { filesService } = await import('../services/filesService.js');
    const uploaded = await filesService.uploadFile(file, {
        kind: 'gedcom',
        familyName: familyName || null,
    });
    if (!uploaded?.id) {
        throw new Error('files-service n\'a pas retourné d\'id');
    }

    // The freshly-uploaded file becomes the SPA's current GEDCOM. Stable
    // file_id (not filename) is now the key for downstream features like
    // the Photos tab group lookup.
    configStore.setCurrentFileId(uploaded.id);

    notificationManager.success(`✅ Arbre sauvegardé dans le cloud (${fileName})`);
    return uploaded;
}